/**
 * Ops Center Dashboard v2 — graphical real-time view of the VOIP gateway.
 * Designed for a wall-mounted screen in the operations room: dark, high-contrast,
 * with green/amber/red accent colors and pulsing motion on active calls.
 */
import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import {
    Phone, PhoneIncoming, Pulse, Broadcast, UserCircle, ChartLineUp,
    Brain, ThermometerHot, Spinner, CheckCircle, XCircle, ArrowsClockwise,
    MoonStars, SunHorizon,
} from '@phosphor-icons/react';

const fmtMMSS = (s) => {
    const n = parseInt(s || 0, 10);
    const m = Math.floor(n / 60).toString().padStart(2, '0');
    const sc = (n % 60).toString().padStart(2, '0');
    return `${m}:${sc}`;
};
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('cs-CZ') : '—';
const fmtHM = (mins) => {
    const m = parseInt(mins || 0, 10);
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60), r = m % 60;
    return r ? `${h}h ${r}m` : `${h}h`;
};
// Smooth client-side countdown: seconds → "Xh Ym Zs" / "Ym Zs" / "Zs".
const fmtHMS = (totalSec) => {
    const s = Math.max(0, Math.floor(totalSec));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sc = s % 60;
    const pad = (n) => n.toString().padStart(2, '0');
    if (h > 0) return `${h}h ${pad(m)}m ${pad(sc)}s`;
    if (m > 0) return `${m}m ${pad(sc)}s`;
    return `${sc}s`;
};

// ---- Ticking analogue clock SVG (real local time + smooth second hand) ----
const TickingClock = ({ size = 56, color = '#22c55e', dimmed = '#475569' }) => {
    // 50 ms tick → sub-second smooth motion of the second hand.
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 50);
        return () => clearInterval(id);
    }, []);
    const ms = now.getMilliseconds();
    const s = now.getSeconds() + ms / 1000;
    const m = now.getMinutes() + s / 60;
    const h = (now.getHours() % 12) + m / 60;
    const secAng = (s / 60) * 360;
    const minAng = (m / 60) * 360;
    const hourAng = (h / 12) * 360;
    const cx = 50, cy = 50;
    // Polar → cartesian helper (angle 0 = 12 o'clock, clockwise).
    const polar = (angDeg, r) => {
        const a = (angDeg - 90) * (Math.PI / 180);
        return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
    };
    const [hx, hy] = polar(hourAng, 22);
    const [mx, my] = polar(minAng, 32);
    const [sx, sy] = polar(secAng, 36);
    // 12 minute-ticks (every 5 min). Major ones at 12/3/6/9.
    const ticks = Array.from({ length: 12 }, (_, i) => {
        const ang = i * 30;
        const [x1, y1] = polar(ang, 40);
        const [x2, y2] = polar(ang, i % 3 === 0 ? 34 : 37);
        return (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke={i % 3 === 0 ? color : dimmed}
                  strokeWidth={i % 3 === 0 ? 1.6 : 0.8}
                  strokeLinecap="round" opacity={i % 3 === 0 ? 0.85 : 0.5} />
        );
    });
    return (
        <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label="Ticking clock">
            {/* Outer ring */}
            <circle cx={cx} cy={cy} r="44" fill="rgba(0,0,0,0.4)"
                    stroke={color} strokeWidth="1.4" opacity="0.55" />
            <circle cx={cx} cy={cy} r="44" fill="none"
                    stroke={color} strokeWidth="0.6" opacity="0.25"
                    style={{ filter: `drop-shadow(0 0 6px ${color})` }} />
            {ticks}
            {/* Hour hand */}
            <line x1={cx} y1={cy} x2={hx} y2={hy}
                  stroke={color} strokeWidth="3.2" strokeLinecap="round" opacity="0.95" />
            {/* Minute hand */}
            <line x1={cx} y1={cy} x2={mx} y2={my}
                  stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.85" />
            {/* Second hand — slimmer + glowing */}
            <line x1={cx} y1={cy} x2={sx} y2={sy}
                  stroke={color} strokeWidth="1.1" strokeLinecap="round"
                  style={{ filter: `drop-shadow(0 0 3px ${color})` }} />
            {/* Hub */}
            <circle cx={cx} cy={cy} r="2.2" fill={color} />
            <circle cx={cx} cy={cy} r="0.9" fill="#0f172a" />
        </svg>
    );
};

// ---- Operational Status Banner -------------------------------------------
const OperationalStatus = ({ status }) => {
    // Smooth client-side ticking — re-render every 1 s so the countdown feels alive
    // between the 5 s server polls. Baseline is derived from the snapshot's
    // `now_local` ISO; we extrapolate elapsed real time since the snapshot was taken.
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => (t + 1) % 60000), 1000);
        return () => clearInterval(id);
    }, []);

    if (!status) return null;
    const open = status.is_open;
    const c = open ? '#22c55e' : '#f59e0b';
    const bg = open
        ? 'linear-gradient(135deg, rgba(34,197,94,0.12), rgba(0,0,0,0.5))'
        : 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(0,0,0,0.5))';
    const Icon = open ? SunHorizon : MoonStars;

    // Compute live remaining seconds. We anchor on snapshot.now_local timestamp;
    // if for any reason it's missing we fall back to "minutes only".
    const elapsedSinceSnap = status.now_local
        ? Math.max(0, (Date.now() - new Date(status.now_local).getTime()) / 1000)
        : 0;
    const liveUntilCloseSec = status.minutes_until_close != null
        ? Math.max(0, status.minutes_until_close * 60 - elapsedSinceSnap)
        : null;
    const liveUntilOpenSec = status.minutes_until_open != null
        ? Math.max(0, status.minutes_until_open * 60 - elapsedSinceSnap)
        : null;

    // Local "live" wall clock (advances every second even between server polls).
    const liveClock = status.now_local
        ? new Date(new Date(status.now_local).getTime() + elapsedSinceSnap * 1000)
        : new Date();
    // `tick` keeps liveClock displays in sync with re-renders.
    void tick;

    return (
        <div
            data-testid="operational-status-widget"
            className="relative overflow-hidden border p-5"
            style={{
                background: bg,
                borderColor: `${c}55`,
                borderRadius: 2,
                boxShadow: `0 0 24px ${c}22`,
            }}
        >
            <div className="flex items-center gap-5">
                <div
                    className="w-14 h-14 flex items-center justify-center flex-shrink-0"
                    style={{ background: `${c}22`, border: `1px solid ${c}`, borderRadius: 2 }}
                >
                    <Icon size={30} weight="duotone" style={{ color: c }} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.25em] font-bold mb-0.5"
                         style={{ color: c }}>
                        Provozní stav · {status.timezone}
                    </div>
                    <div className="font-heading text-3xl font-bold tracking-tight"
                         data-testid="op-status-label"
                         style={{ color: c }}>
                        {open ? 'OTEVŘENO' : 'ZAVŘENO'}
                    </div>
                    <div className="text-xs mt-1" style={{ color: '#cbd5e1' }}>
                        {open ? (
                            <>
                                Otevírací doba dnes <span className="font-mono" style={{ color: c }}>
                                    {status.today_open} – {status.today_close}
                                </span>
                                {liveUntilCloseSec != null &&
                                    <> · zavírá za <span className="font-mono tabular-nums" data-testid="op-status-until-close" style={{ color: '#e2e8f0' }}>
                                        {fmtHMS(liveUntilCloseSec)}
                                    </span></>}
                                · IVR routuje volby 1/2/3 standardně
                            </>
                        ) : (
                            <>
                                Hovory přesměrovány na <strong style={{ color: '#fbbf24' }}>after-hours kaskádu</strong>
                                {status.next_open_day_name && liveUntilOpenSec != null && (
                                    <> · otevíráme <span data-testid="op-status-next-open" className="tabular-nums" style={{ color: '#e2e8f0' }}>
                                        {status.next_open_day_name} (za <span className="font-mono">{fmtHMS(liveUntilOpenSec)}</span>)
                                    </span></>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <div className="flex flex-col items-center gap-1.5 flex-shrink-0"
                     data-testid="op-status-ticking-clock">
                    <TickingClock size={56} color={c} dimmed="#334155" />
                    <div className="font-mono text-xs tabular-nums" style={{ color: '#e2e8f0' }}>
                        {liveClock.toLocaleTimeString('cs-CZ')}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ---- Tile (KPI box) ---------------------------------------------------------
const Kpi = ({ label, value, sub, accent = '#22c55e', icon: Icon, testId }) => (
    <div
        data-testid={testId}
        className="relative overflow-hidden border p-5"
        style={{
            background: 'linear-gradient(135deg, rgba(34,197,94,0.05), rgba(0,0,0,0.4))',
            borderColor: 'rgba(34,197,94,0.2)',
            borderRadius: 2,
        }}
    >
        <div className="flex items-start justify-between mb-2">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: '#94a3b8' }}>
                {label}
            </span>
            {Icon && <Icon size={18} style={{ color: accent }} />}
        </div>
        <div className="text-3xl font-mono font-bold tracking-tight" style={{ color: accent }}>
            {value}
        </div>
        {sub && <div className="text-[11px] mt-1" style={{ color: '#64748b' }}>{sub}</div>}
    </div>
);

// ---- Call Flow Card --------------------------------------------------------
const CallFlowCard = ({ call }) => {
    const stages = [
        { label: 'Volající', value: call.caller_id || '—', icon: PhoneIncoming },
        { label: 'DID', value: call.did_number || '—', icon: Broadcast },
        { label: 'Cíl', value: call.destination || 'IVR', icon: Phone },
    ];
    return (
        <div
            data-testid={`active-call-${call.id}`}
            className="border p-4"
            style={{
                background: 'rgba(34,197,94,0.04)',
                borderColor: '#22c55e',
                borderRadius: 2,
                boxShadow: '0 0 20px rgba(34,197,94,0.15)',
            }}
        >
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ background: '#22c55e', animation: 'pulse 1.2s ease-in-out infinite' }}
                    />
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#22c55e' }}>
                        Aktivní · {fmtTime(call.started_at)}
                    </span>
                </div>
                <span className="font-mono text-lg font-bold" style={{ color: '#22c55e' }}>
                    {fmtMMSS(call.live_duration_seconds)}
                </span>
            </div>
            <div className="flex items-center gap-2 overflow-x-auto">
                {stages.map((s, i) => (
                    <React.Fragment key={i}>
                        <div className="flex-1 min-w-0">
                            <div className="text-[9px] uppercase tracking-wider" style={{ color: '#64748b' }}>
                                {s.label}
                            </div>
                            <div className="font-mono text-sm truncate" style={{ color: '#e2e8f0' }}>
                                {s.value}
                            </div>
                        </div>
                        {i < stages.length - 1 && (
                            <div className="text-xl" style={{ color: '#22c55e', opacity: 0.5 }}>→</div>
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

// ---- Extension Tile --------------------------------------------------------
const ExtensionTile = ({ ext }) => {
    const stateColors = {
        incall:   { c: '#22c55e', label: 'V HOVORU', glow: '0 0 16px rgba(34,197,94,0.4)' },
        ringing:  { c: '#f59e0b', label: 'ZVONÍ',    glow: '0 0 16px rgba(245,158,11,0.4)' },
        idle:     { c: '#64748b', label: 'IDLE',     glow: 'none' },
        offline:  { c: '#475569', label: 'OFFLINE',  glow: 'none' },
    };
    const s = stateColors[ext.live_status] || stateColors.idle;
    return (
        <div
            data-testid={`ext-tile-${ext.extension}`}
            className="border p-3 flex flex-col items-center"
            style={{
                background: 'rgba(0,0,0,0.3)',
                borderColor: s.c,
                borderRadius: 2,
                boxShadow: s.glow,
                transition: 'box-shadow 200ms',
            }}
        >
            <UserCircle size={22} weight="duotone" style={{ color: s.c }} />
            <div className="font-mono text-2xl font-bold mt-1" style={{ color: '#e2e8f0' }}>
                {ext.extension}
            </div>
            <div className="text-[10px] mt-0.5 truncate max-w-full" style={{ color: '#94a3b8' }}>
                {ext.display_name || '—'}
            </div>
            <div
                className="text-[9px] font-bold uppercase tracking-wider mt-1.5 px-1.5 py-0.5"
                style={{ background: `${s.c}22`, color: s.c, borderRadius: 2 }}
            >
                {s.label}
            </div>
        </div>
    );
};

// ---- Trunk Tile ------------------------------------------------------------
const TrunkTile = ({ trunk }) => {
    const ok = trunk.registered;
    const c = ok ? '#22c55e' : '#ef4444';
    return (
        <div
            data-testid={`trunk-tile-${trunk.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            className="border p-4"
            style={{
                background: 'rgba(0,0,0,0.3)',
                borderColor: `${c}55`,
                borderRadius: 2,
            }}
        >
            <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-sm" style={{ color: '#e2e8f0' }}>
                    {trunk.name}
                </span>
                {ok
                    ? <CheckCircle size={18} weight="fill" style={{ color: c }} />
                    : <XCircle size={18} weight="fill" style={{ color: c }} />}
            </div>
            <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: '#64748b' }}>
                {trunk.kind} · {trunk.host}
            </div>
            <div className="font-mono text-xs" style={{ color: '#94a3b8' }}>
                DID: {trunk.did_number || '—'}
            </div>
            <div className="text-[10px] mt-1.5" style={{ color: ok ? c : '#ef4444' }}>
                {ok ? 'REGISTRED' : 'DOWN'}{trunk.last_check ? ` · ${fmtTime(trunk.last_check)}` : ''}
            </div>
        </div>
    );
};

// ---- Sparkline (SVG, 60 mins) ----------------------------------------------
const Sparkline = ({ data }) => {
    if (!data || !data.length) return null;
    const max = Math.max(1, ...data.map(d => d.count));
    const W = 800, H = 60;
    const step = W / Math.max(1, data.length - 1);
    const points = data.map((d, i) => `${i * step},${H - (d.count / max) * (H - 4) - 2}`);
    const polyline = points.join(' ');
    const area = `0,${H} ${polyline} ${W},${H}`;
    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" style={{ height: 60 }}>
            <polygon points={area} fill="rgba(34,197,94,0.15)" />
            <polyline points={polyline} fill="none" stroke="#22c55e" strokeWidth="1.5" />
        </svg>
    );
};

// ---- 7×24 Heat-map (SVG) ---------------------------------------------------
const Heatmap = ({ grid }) => {
    if (!grid || !grid.length) return null;
    const max = Math.max(1, ...grid.map(g => g.count));
    const cellW = 22, cellH = 16, gap = 2;
    const W = (cellW + gap) * 24, H = (cellH + gap) * 7;
    const day = ['Po','Út','St','Čt','Pá','So','Ne'];
    return (
        <div className="overflow-x-auto">
            <svg width={W + 32} height={H + 20}>
                {day.map((d, i) => (
                    <text key={i} x={0} y={i * (cellH + gap) + cellH * 0.75 + 14}
                          fontSize="9" fill="#64748b" fontFamily="monospace">{d}</text>
                ))}
                {[0, 6, 12, 18].map(h => (
                    <text key={h} x={32 + h * (cellW + gap)} y={H + 18}
                          fontSize="9" fill="#64748b" fontFamily="monospace">{`${h}h`}</text>
                ))}
                {grid.map((g, idx) => {
                    const intensity = g.count / max;
                    const x = 32 + g.hour * (cellW + gap);
                    const y = g.weekday * (cellH + gap) + 14;
                    const fill = g.count === 0
                        ? 'rgba(255,255,255,0.04)'
                        : `rgba(34,197,94,${0.15 + intensity * 0.75})`;
                    return (
                        <rect key={idx} x={x} y={y} width={cellW} height={cellH}
                              fill={fill} rx="1"
                              data-testid={`heat-${g.weekday}-${g.hour}`}>
                            <title>{`${day[g.weekday]} ${g.hour}:00 — ${g.count} hovorů`}</title>
                        </rect>
                    );
                })}
            </svg>
        </div>
    );
};

// ---- Donut (AI intents) ----------------------------------------------------
const Donut = ({ data }) => {
    if (!data || !data.length) {
        return (
            <div className="text-center text-xs p-4" style={{ color: '#64748b' }}>
                Žádné AI intenty za posledních 24 h
            </div>
        );
    }
    const total = data.reduce((s, d) => s + d.count, 0) || 1;
    const colors = ['#22c55e', '#0ea5e9', '#f59e0b', '#ef4444', '#a855f7', '#ec4899', '#14b8a6', '#84cc16'];
    let cum = 0;
    const R = 60, r = 35, cx = 80, cy = 80;
    return (
        <div className="flex items-center gap-4">
            <svg width="160" height="160" viewBox="0 0 160 160">
                <circle cx={cx} cy={cy} r={R} fill="rgba(0,0,0,0.3)" />
                {data.map((d, i) => {
                    const a0 = (cum / total) * 2 * Math.PI - Math.PI / 2;
                    cum += d.count;
                    const a1 = (cum / total) * 2 * Math.PI - Math.PI / 2;
                    const large = a1 - a0 > Math.PI ? 1 : 0;
                    const x0 = cx + R * Math.cos(a0), y0 = cy + R * Math.sin(a0);
                    const x1 = cx + R * Math.cos(a1), y1 = cy + R * Math.sin(a1);
                    const xi0 = cx + r * Math.cos(a1), yi0 = cy + r * Math.sin(a1);
                    const xi1 = cx + r * Math.cos(a0), yi1 = cy + r * Math.sin(a0);
                    const path = `M ${x0} ${y0} A ${R} ${R} 0 ${large} 1 ${x1} ${y1} L ${xi0} ${yi0} A ${r} ${r} 0 ${large} 0 ${xi1} ${yi1} Z`;
                    return <path key={i} d={path} fill={colors[i % colors.length]} />;
                })}
                <text x={cx} y={cy - 4} textAnchor="middle" fontSize="22" fontWeight="bold" fill="#e2e8f0">
                    {total}
                </text>
                <text x={cx} y={cy + 12} textAnchor="middle" fontSize="9" fill="#64748b"
                      style={{ letterSpacing: '0.15em' }}>
                    INTENTŮ
                </text>
            </svg>
            <div className="flex-1 space-y-1 text-xs">
                {data.map((d, i) => (
                    <div key={i} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="w-2 h-2 flex-shrink-0" style={{ background: colors[i % colors.length] }} />
                            <span className="truncate" style={{ color: '#cbd5e1' }}>{d.intent}</span>
                        </div>
                        <span className="font-mono" style={{ color: '#94a3b8' }}>{d.count}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ============================================================================
const OpsDashboard = () => {
    const [snap, setSnap] = useState(null);
    const [wsStatus, setWsStatus] = useState('connecting');
    const wsRef = useRef(null);
    const pollRef = useRef(null);

    // Pull snapshot every 5s
    const fetchSnap = async () => {
        try {
            const r = await api.get('/dashboard/snapshot');
            setSnap(r.data);
        } catch (e) {
            // backend down or auth issue — surface as offline
            setWsStatus('error');
        }
    };

    useEffect(() => {
        fetchSnap();
        pollRef.current = setInterval(fetchSnap, 5000);
        return () => { clearInterval(pollRef.current); };
    }, []);

    // Live WS for snappier active-call updates
    const connectWs = React.useCallback(() => {
        const token = localStorage.getItem('vg_token');
        if (!token) { setWsStatus('no_token'); return; }
        const backend = process.env.REACT_APP_BACKEND_URL || '';
        const proto = backend.startsWith('https') ? 'wss' : 'ws';
        const host = backend.replace(/^https?:\/\//, '');
        const url = `${proto}://${host}/api/ws/active-calls?token=${encodeURIComponent(token)}`;
        try {
            const ws = new WebSocket(url);
            wsRef.current = ws;
            setWsStatus('connecting');
            ws.onopen = () => setWsStatus('connected');
            ws.onmessage = (e) => {
                try {
                    const msg = JSON.parse(e.data);
                    if (msg.type === 'ami-event') {
                        // Live AMI push — annotate snapshot with a "last_ami_event"
                        // ribbon and force a fresh poll for the affected call.
                        setSnap(prev => prev ? {
                            ...prev,
                            last_ami_event: { name: msg.event, ts: msg.ts, uid: msg.uniqueid, caller: msg.caller_id_num, peer: msg.peer, peer_status: msg.peer_status },
                        } : prev);
                        // For events that change active_calls list (DialBegin/Hangup/BridgeEnter),
                        // refresh the snapshot immediately rather than waiting 5s.
                        if (['DialBegin', 'DialEnd', 'Hangup', 'BridgeEnter', 'BridgeLeave', 'Newchannel'].includes(msg.event)) {
                            fetchSnap();
                        }
                        return;
                    }
                    if (msg.active) {
                        setSnap(prev => prev ? { ...prev, active_calls: msg.active } : prev);
                    }
                } catch (_) { /* ignore parse error */ }
            };
            ws.onclose = () => {
                setWsStatus('disconnected');
                setTimeout(() => { if (wsRef.current === ws) connectWs(); }, 3000);
            };
            ws.onerror = () => setWsStatus('error');
        } catch (_) { setWsStatus('error'); }
    }, []);
    useEffect(() => { connectWs(); return () => { try { wsRef.current?.close(); } catch (_) { /* ignore */ } }; }, [connectWs]);

    if (!snap) {
        return (
            <div className="flex items-center justify-center h-96 gap-2" style={{ color: '#22c55e' }}>
                <Spinner size={22} className="animate-spin" /> Načítám live snapshot…
            </div>
        );
    }

    return (
        <div data-testid="ops-dashboard" className="space-y-5">
            {/* HEADER */}
            <div className="flex items-end justify-between border-b pb-4"
                 style={{ borderColor: 'rgba(34,197,94,0.2)' }}>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] font-bold mb-1"
                         style={{ color: '#22c55e' }}>
                        Ops Center · Live
                    </div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight" style={{ color: '#e2e8f0' }}>
                        Real-time Monitor v2
                    </h1>
                    <div className="text-xs mt-1 font-mono" style={{ color: '#64748b' }}>
                        snapshot · {fmtTime(snap.timestamp)} · refresh 5s
                    </div>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider"
                     data-testid="ops-ws-status">
                    {wsStatus === 'connected'   && <><CheckCircle size={14} weight="fill" color="#22c55e" /><span style={{ color: '#22c55e' }}>LIVE</span></>}
                    {wsStatus === 'connecting'  && <><Spinner size={14} className="animate-spin" /><span>CONNECTING</span></>}
                    {(wsStatus === 'disconnected' || wsStatus === 'error') &&
                        <><XCircle size={14} weight="fill" color="#ef4444" />
                          <span style={{ color: '#ef4444' }}>OFFLINE</span>
                          <button onClick={connectWs} data-testid="ops-reconnect-btn"
                                  className="ml-2 h-7 px-2 border text-[10px]"
                                  style={{ borderColor: '#ef4444' }}>
                              <ArrowsClockwise size={10} className="inline mr-1" /> RECONNECT
                          </button></>}
                </div>
            </div>

            {/* OPERATIONAL STATUS BANNER */}
            <OperationalStatus status={snap.operational_status} />

            {/* KPI ROW */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <Kpi testId="kpi-active" label="Aktivní hovory" value={snap.kpis.active_now} icon={Pulse} />
                <Kpi testId="kpi-24h"    label="Hovorů 24 h"    value={snap.kpis.calls_24h} icon={Phone} />
                <Kpi testId="kpi-answer" label="Answer rate"    value={`${snap.kpis.answer_rate_pct}%`} accent="#0ea5e9" icon={CheckCircle} />
                <Kpi testId="kpi-avgdur" label="Avg trvání"     value={fmtMMSS(snap.kpis.avg_duration_seconds)} accent="#a855f7" icon={ChartLineUp} />
                <Kpi testId="kpi-ans60"  label="Přijato 60 m"   value={snap.kpis.answered_60m} accent="#22c55e" />
                <Kpi testId="kpi-mis60"  label="Zmeškáno 60 m"  value={snap.kpis.missed_60m} accent="#f59e0b" />
            </div>

            {/* ACTIVE CALLS */}
            <section data-testid="active-calls-section">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-2 flex items-center gap-2"
                     style={{ color: '#22c55e' }}>
                    <Pulse size={12} /> Aktivní hovory · {snap.active_calls.length}
                </div>
                {snap.active_calls.length === 0 ? (
                    <div className="border p-6 text-center text-sm"
                         style={{ borderColor: 'rgba(34,197,94,0.15)', color: '#64748b', background: 'rgba(0,0,0,0.2)' }}>
                        Žádné aktivní hovory
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {snap.active_calls.map(c => <CallFlowCard key={c.id} call={c} />)}
                    </div>
                )}
            </section>

            {/* MAIN GRID */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* EXTENSIONS */}
                <section className="lg:col-span-2" data-testid="extensions-section">
                    <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-2 flex items-center gap-2"
                         style={{ color: '#0ea5e9' }}>
                        <UserCircle size={12} /> SIP klapky · {snap.extensions.length}
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                        {snap.extensions.map(e => <ExtensionTile key={e.id || e.extension} ext={e} />)}
                        {snap.extensions.length === 0 && (
                            <div className="col-span-full text-xs text-center p-4" style={{ color: '#64748b' }}>
                                Žádné aktivní klapky
                            </div>
                        )}
                    </div>
                </section>
                {/* TRUNKS */}
                <section data-testid="trunks-section">
                    <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-2 flex items-center gap-2"
                         style={{ color: '#f59e0b' }}>
                        <Broadcast size={12} /> Trunky
                    </div>
                    <div className="grid gap-2">
                        {snap.trunks.map((t, i) => <TrunkTile key={i} trunk={t} />)}
                        {snap.trunks.length === 0 && (
                            <div className="text-xs text-center p-4" style={{ color: '#64748b' }}>
                                Žádné nakonfigurované trunky
                            </div>
                        )}
                    </div>
                </section>
            </div>

            {/* SPARKLINE */}
            <section data-testid="sparkline-section"
                     className="border p-4"
                     style={{ borderColor: 'rgba(34,197,94,0.15)', background: 'rgba(0,0,0,0.2)' }}>
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-2 flex items-center gap-2"
                     style={{ color: '#22c55e' }}>
                    <ChartLineUp size={12} /> Hovory za posledních 60 min (1-min buckety)
                </div>
                <Sparkline data={snap.sparkline_60m} />
                <div className="flex justify-between mt-1 text-[10px] font-mono" style={{ color: '#64748b' }}>
                    <span>−60 min</span><span>−30 min</span><span>−0 min (teď)</span>
                </div>
            </section>

            {/* HEATMAP + DONUT */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <section data-testid="heatmap-section"
                         className="border p-4"
                         style={{ borderColor: 'rgba(245,158,11,0.15)', background: 'rgba(0,0,0,0.2)' }}>
                    <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-2 flex items-center gap-2"
                         style={{ color: '#f59e0b' }}>
                        <ThermometerHot size={12} /> Heat-mapa hodin · posledních 30 dní
                    </div>
                    <Heatmap grid={snap.heatmap_7x24} />
                </section>
                <section data-testid="intents-section"
                         className="border p-4"
                         style={{ borderColor: 'rgba(168,85,247,0.15)', background: 'rgba(0,0,0,0.2)' }}>
                    <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-3 flex items-center gap-2"
                         style={{ color: '#a855f7' }}>
                        <Brain size={12} /> AI intent breakdown · posledních 24 h
                    </div>
                    <Donut data={snap.top_intents_24h} />
                </section>
            </div>

            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(34,197,94,0.7); }
                    50%     { opacity: 0.7; box-shadow: 0 0 0 6px rgba(34,197,94,0); }
                }
            `}</style>
        </div>
    );
};

export default OpsDashboard;
